// Mobile upload token DAO — reads upload_tokens + the joined transaction.
// Used by the public-link mobile-upload screen (operator shares a link,
// driver uploads a photo through it).

import { BaseDao, type SupabaseClient } from '../base.ts';
import type { PostgrestError } from '@supabase/supabase-js';

interface UploadTokenRow {
  transaction_id: number;
  expires_at: string;
}

export interface MobileUploadTokenContext {
  transaction_id: number;
  reference_number: string;
  load_company: string | null;
  unload_company: string | null;
}

export type MobileUploadTokenError = 'invalid' | 'expired';

export class MobileUploadTokenDao extends BaseDao<UploadTokenRow> {
  protected readonly dependentResources = ['upload_tokens'];

  constructor(sb: SupabaseClient) {
    super(sb, 'upload_tokens');
  }

  /** Resolve a token string to the context the mobile screen needs.
   *  Returns either the context or a discriminated error code. */
  async resolveByToken(
    token: string,
  ): Promise<MobileUploadTokenContext | { error: MobileUploadTokenError }> {
    const { data: tokenRow, error }: { data: UploadTokenRow | null; error: PostgrestError | null } = await this.sb
      .from('upload_tokens')
      .select('transaction_id, expires_at')
      .eq('token', token)
      .single();
    if (error || !tokenRow) return { error: 'invalid' };
    if (new Date(tokenRow.expires_at) < new Date()) return { error: 'expired' };

    const { data: tx } = await this.sb
      .from('transactions')
      .select(
        'id, reference_number, load_place_company:load_place_company_id(short_name), unload_place_company:unload_place_company_id(short_name)',
      )
      .eq('id', tokenRow.transaction_id)
      .single();

    if (!tx) return { error: 'invalid' };

    return {
      transaction_id: (tx as { id: number }).id,
      reference_number: (tx as { reference_number: string | null }).reference_number ?? `#${(tx as { id: number }).id}`,
      load_company: ((tx as Record<string, unknown>).load_place_company as { short_name?: string } | null)?.short_name ?? null,
      unload_company: ((tx as Record<string, unknown>).unload_place_company as { short_name?: string } | null)?.short_name ?? null,
    };
  }
}
